import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import clsx from 'clsx';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Slider } from './ui/slider';

const METADATA_URL = 'https://api.ourworldindata.org/v1/indicators/1077602.metadata.json';
const DATA_URL = 'https://api.ourworldindata.org/v1/indicators/1077602.data.json';
const CITYSCAPE_DARKEST_GREEN = '#2b4331';

const CarbonIntensityLookup = ({ onApplyValue, className }) => {
  const [countries, setCountries] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [startYear, setStartYear] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const [chartWidth, setChartWidth] = useState(640);

  const suggestionsRef = useRef(null);
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      try {
        const [metaResponse, dataResponse] = await Promise.all([
          fetch(METADATA_URL, { signal: controller.signal }),
          fetch(DATA_URL, { signal: controller.signal })
        ]);

        if (!metaResponse.ok) {
          throw new Error(`Metadata request failed (${metaResponse.status})`);
        }
        if (!dataResponse.ok) {
          throw new Error(`Dataset request failed (${dataResponse.status})`);
        }

        const [metaJson, dataJson] = await Promise.all([
          metaResponse.json(),
          dataResponse.json()
        ]);

        if (!isMounted) {
          return;
        }

        const entityValues = metaJson?.dimensions?.entities?.values ?? [];
        const sortedCountries = entityValues
          .filter((item) => item?.name)
          .map((item) => ({
            id: item.id,
            name: item.name,
            code: item.code ?? '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setCountries(sortedCountries);
        setRawData({
          values: dataJson?.values ?? [],
          years: dataJson?.years ?? [],
          entityIds: dataJson?.entities ?? [],
        });
        setError(null);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Failed to load carbon intensity dataset', err);
        setError('Unable to load carbon intensity data. Please try again later.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!suggestionsRef.current) {
        return;
      }
      if (!suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (!containerRef.current) {
        return;
      }
      const nextWidth = containerRef.current.clientWidth ?? 640;
      setChartWidth(Math.max(nextWidth, 320));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const seriesByEntity = useMemo(() => {
    if (!rawData) {
      return new Map();
    }

    const { values, years, entityIds } = rawData;
    const map = new Map();

    values.forEach((value, index) => {
      const entityId = entityIds[index];
      const year = years[index];

      if (value === null || typeof value === 'undefined' || typeof year === 'undefined') {
        return;
      }

      if (!map.has(entityId)) {
        map.set(entityId, []);
      }
      map.get(entityId).push({ year, value });
    });

    map.forEach((entries) => {
      entries.sort((a, b) => a.year - b.year);
    });

    return map;
  }, [rawData]);

  const selectedSeriesFull = useMemo(() => {
    if (!selectedCountry) {
      return [];
    }
    return seriesByEntity.get(selectedCountry.id) ?? [];
  }, [selectedCountry, seriesByEntity]);

  useEffect(() => {
    if (!selectedSeriesFull.length) {
      setStartYear(null);
      setEndYear(null);
      return;
    }

    const firstYear = selectedSeriesFull[0].year;
    const lastYear = selectedSeriesFull[selectedSeriesFull.length - 1].year;
    setStartYear(firstYear);
    setEndYear(lastYear);
  }, [selectedSeriesFull]);

  const filteredCountries = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return [];
    }
    return countries
      .filter((country) =>
        country.name.toLowerCase().includes(term) ||
        (country.code && country.code.toLowerCase().includes(term))
      )
      .slice(0, 20);
  }, [countries, query]);

  const handleCountrySelect = useCallback((country) => {
    setSelectedCountry(country);
    setQuery(country.name);
    setShowSuggestions(false);
  }, []);

  const allYearsForCountry = useMemo(() => selectedSeriesFull.map((entry) => entry.year), [selectedSeriesFull]);

  const filteredSeries = useMemo(() => {
    if (!selectedSeriesFull.length || startYear === null || endYear === null) {
      return [];
    }
    return selectedSeriesFull.filter((entry) => entry.year >= startYear && entry.year <= endYear);
  }, [selectedSeriesFull, startYear, endYear]);

  const sliderDomain = useMemo(() => {
    if (!selectedSeriesFull.length) {
      return [0, 1];
    }
    const minYear = selectedSeriesFull[0].year;
    const maxYear = selectedSeriesFull[selectedSeriesFull.length - 1].year;
    return [minYear, maxYear];
  }, [selectedSeriesFull]);
  const sliderDisabled = !selectedSeriesFull.length;
  const [sliderMin, sliderMax] = sliderDomain;

  const handleYearRangeChange = useCallback((value) => {
    if (!value || value.length < 2 || sliderDisabled) {
      return;
    }
    const [rawStart, rawEnd] = value;
    const clampedStart = Math.max(sliderMin, Math.min(Math.round(rawStart), sliderMax));
    const clampedEndRaw = Math.max(sliderMin, Math.min(Math.round(rawEnd), sliderMax));
    const clampedEnd = Math.max(clampedStart, clampedEndRaw);

    if (startYear != clampedStart) {
      setStartYear(clampedStart);
    }
    if (endYear != clampedEnd) {
      setEndYear(clampedEnd);
    }
  }, [sliderDisabled, sliderMin, sliderMax, startYear, endYear]);

  useEffect(() => {
    const svgNode = svgRef.current;
    if (!svgNode) {
      return;
    }

    const containerNode = svgNode.parentElement;
    let tooltip = null;

    if (containerNode) {
      const containerSelection = d3.select(containerNode);
      containerSelection.selectAll('.carbon-intensity-tooltip').remove();

      tooltip = containerSelection
        .append('div')
        .attr('class', 'carbon-intensity-tooltip pointer-events-none absolute rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-md')
        .style('opacity', 0);
    }

    const svg = d3.select(svgNode);
    svg.selectAll('*').remove();

    if (!filteredSeries.length) {
      svg
        .append('text')
        .attr('x', '50%')
        .attr('y', '50%')
        .attr('text-anchor', 'middle')
        .attr('fill', '#6b7280')
        .attr('font-size', 14)
        .text(selectedCountry ? 'No data for selected years' : 'Select a country to preview data');

      if (tooltip) {
        tooltip.remove();
      }
      return;
    }

    const valueFormatter = d3.format(',.2f');

    const showTooltip = (event, datum) => {
      if (!tooltip || !containerNode) {
        return;
      }
      const rect = containerNode.getBoundingClientRect();
      const left = event.clientX - rect.left;
      const top = event.clientY - rect.top;

      tooltip
        .style('opacity', 1)
        .style('left', `${left + 12}px`)
        .style('top', `${top - 36}px`)
        .text(`${datum.year}: ${valueFormatter(datum.value)} gCO2/kWh`);
    };

    const hideTooltip = () => {
      if (tooltip) {
        tooltip.style('opacity', 0);
      }
    };

    const height = 320;
    const margin = { top: 24, right: 32, bottom: 48, left: 64 };
    const innerWidth = Math.max(chartWidth - margin.left - margin.right, 120);
    const innerHeight = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${chartWidth} ${height}`);
    svg.attr('preserveAspectRatio', 'xMidYMid meet');

    const x = d3
      .scaleLinear()
      .domain(d3.extent(filteredSeries, (d) => d.year))
      .range([0, innerWidth]);

    const y = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(filteredSeries, (d) => d.value) * 1.1
      ])
      .nice()
      .range([innerHeight, 0]);

    const root = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    root
      .append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).tickFormat(d3.format('d')))
      .selectAll('text')
      .attr('transform', 'rotate(-30)')
      .style('text-anchor', 'end')
      .attr('fill', '#000000');

    root
      .append('g')
      .call(d3.axisLeft(y).ticks(6))
      .selectAll('text')
      .attr('fill', '#000000');

    root
      .append('path')
      .datum(filteredSeries)
      .attr('fill', 'none')
      .attr('stroke', CITYSCAPE_DARKEST_GREEN)
      .attr('stroke-width', 2)
      .attr(
        'd',
        d3
          .line()
          .x((d) => x(d.year))
          .y((d) => y(d.value))
          .curve(d3.curveMonotoneX)
      );

    root
      .append('g')
      .attr('class', 'data-points')
      .selectAll('circle')
      .data(filteredSeries)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(d.year))
      .attr('cy', (d) => y(d.value))
    .attr('r', 3)
      .attr('fill', CITYSCAPE_DARKEST_GREEN)
      .on('mouseenter', (event, datum) => showTooltip(event, datum))
      .on('mousemove', (event, datum) => showTooltip(event, datum))
      .on('mouseleave', () => hideTooltip());

    const lastPoint = filteredSeries[filteredSeries.length - 1];

    root
      .append('text')
      .attr('x', x(lastPoint.year))
    .attr('y', y(lastPoint.value) + 16)
      .attr('text-anchor', 'end')
      .attr('fill', '#111827')
      .attr('font-size', 12)
      .text(`${lastPoint.year}: ${valueFormatter(lastPoint.value)} gCO2/kWh`);

    root
      .append('text')
      .attr('x', innerWidth / 2)
    .attr('y', innerHeight + 48)
      .attr('text-anchor', 'middle')
      .attr('fill', '#111827')
      .text('Year');

    root
      .append('text')
      .attr('x', -innerHeight / 2)
      .attr('y', -48)
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'middle')
      .attr('fill', '#111827')
      .text('gCO2 per kWh');

    return () => {
      svg.selectAll('*').remove();
      if (tooltip) {
        tooltip.remove();
      }
    };
  }, [filteredSeries, selectedCountry, chartWidth]);

  const latestPoint = useMemo(() => {
    if (!filteredSeries.length) {
      return null;
    }
    return filteredSeries[filteredSeries.length - 1];
  }, [filteredSeries]);

  const handleApplyLatest = useCallback(() => {
    if (!latestPoint || !selectedCountry || !onApplyValue) {
      return;
    }
    const valueKg = latestPoint.value / 1000;
    onApplyValue({
      country: selectedCountry,
      year: latestPoint.year,
      valueKg,
      valueG: latestPoint.value,
    });
  }, [latestPoint, onApplyValue, selectedCountry]);

  const sliderValue = startYear !== null && endYear !== null
    ? [startYear, endYear]
    : [sliderMin, sliderMax];

  const uniqueSuggestions = filteredCountries;

  return (
    <div className={clsx('space-y-3', className)} ref={containerRef}>
      <p className="text-xs text-center text-black -mt-4 mb-2">
        Data source: <span className="cursor-help underline decoration-dotted" title="Ember (2025); Energy Institute - Statistical Review of World Energy (2025) - with major processing by Our World in Data">ourworldindata.org</span>
      </p>
      <div>
        <Label htmlFor="carbon-intensity-search" className="mb-2 block text-sm font-medium text-black">
          Search by country
        </Label>
        <div className="relative" ref={suggestionsRef}>
          <Input
            id="carbon-intensity-search"
            value={query}
            onChange={(event) => {
              const nextValue = event.target.value;
              setQuery(nextValue);
              setShowSuggestions(Boolean(nextValue.trim()));
            }}
            onFocus={() => setShowSuggestions(Boolean(query.trim()))}
            placeholder="Start typing a country (e.g., France)"
            disabled={loading || !!error}
            className="text-black placeholder:text-black/50"
          />
          {showSuggestions && uniqueSuggestions.length > 0 && (
            <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {uniqueSuggestions.map((country) => (
                <li key={country.id}>
                  <button
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className="flex w-full items-center justify-between px-3 py-2 text-sm text-black hover:bg-slate-100"
                  >
                    <span>{country.name}</span>
                    {country.code ? (
                      <span className="ml-4 text-xs uppercase text-black">{country.code}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="mt-2 text-xs text-black">
          {selectedCountry
            ? `Selected: ${selectedCountry.name}${selectedCountry.code ? ` (${selectedCountry.code})` : ''}`
            : 'Start typing to search the Our World in Data entities list.'}
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-[240px] w-full" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="mb-2 mt-6 block text-sm font-medium text-black" htmlFor="carbon-intensity-year-range">
              Year range
            </Label>
            {sliderDisabled ? (
              <div className="rounded-md border border-slate-200 bg-white px-3 py-6 text-center text-xs text-black">
                Select a country to preview year range. The slider activates once data is available.
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-white px-3 py-4">
                <Slider
                  id="carbon-intensity-year-range"
                  value={sliderValue}
                  onValueChange={handleYearRangeChange}
                  min={sliderDomain[0]}
                  max={sliderDomain[1]}
                  step={1}
                  disabled={sliderDisabled}
                  aria-disabled={sliderDisabled}
                  className="mt-2"
                />
                <div className="mt-3 flex items-center justify-between text-xs text-black">
                  <span>{startYear}</span>
                  <span>{endYear}</span>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-center text-black">
            Applying gco2/kWh values converts it to kgco2/kWh for the grid intensity field.
          </div>

          <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white">
            <svg ref={svgRef} className="h-[320px] w-full" />
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-black">
              {latestPoint ? (
                <>
                  Latest value in range ({latestPoint.year}):{' '}
                  <span className="font-semibold text-black">
                    {d3.format(',.2f')(latestPoint.value)} gCO2/kWh
                  </span>{' '}
                  ({d3.format(',.4f')(latestPoint.value / 1000)} kgCO2/kWh)
                </>
              ) : (
                'Select a country and adjust the year range to enable apply.'
              )}
            </div>
            <Button
              type="button"
              onClick={handleApplyLatest}
              disabled={!latestPoint || !selectedCountry}
            >
              Use latest value
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarbonIntensityLookup;
